// Modality View - first step of /onboarding. Receives the initial selection +
// submit/back callbacks from the parent Onboarding view. Internal
// selection/search/showAll state lives in useModalityViewModel. The selection
// is persisted as an onboarding response, so submission cannot fail locally.

import { clsx } from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { PlaybookLogo } from '@design-system/components/logo/PlaybookLogo';
import { Typography } from '@design-system/components/typography/typography';
import { useModalityViewModel } from '../view-model/Modality.viewModel';
import * as styles from './Modality.view.css';

export type ModalityViewProps = {
  initialKeywords: readonly string[];
  onSubmit: (keywords: readonly string[]) => void;
  onBack: () => void;
  canGoBack: boolean;
  displayStep: number;
  totalDisplaySteps: number;
};

export const ModalityView = ({
  initialKeywords,
  onSubmit,
  onBack,
  canGoBack,
}: ModalityViewProps) => {
  const vm = useModalityViewModel(initialKeywords);
  const { state } = vm;

  return (
    <div className={styles.pageContainer} data-testid="modality-view">
      <header className={styles.topHeader}>
        <PlaybookLogo showGlyph showName height={24} color="N-100" />
      </header>

      <div className={styles.content}>
        {canGoBack ? (
          <div className={styles.backRow}>
            <button
              type="button"
              className={styles.backButton}
              onClick={onBack}
              data-testid="back-button"
              aria-label="Go back"
            >
              <span className={styles.backIcon}>←</span>
            </button>
          </div>
        ) : null}

        <div className={styles.titleSection}>
          <h1 className={styles.title}>What best describes your content?</h1>
          <p className={styles.subtitle}>
            Pick up to 5 so the right people find you on Playbook. Your first
            pick is your primary category.
          </p>
        </div>

        <div className={styles.searchRow}>
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            className={styles.searchIcon}
            aria-hidden
          />
          <input
            type="search"
            className={styles.searchInput}
            placeholder="Search categories"
            value={state.search}
            onChange={(event) => vm.dispatch('Search', event.target.value)}
            aria-label="Search modality categories"
            data-testid="modality-search"
          />
        </div>

        {state.visibleChips.length === 0 ? (
          <div className={styles.emptyState}>
            No matches. Try a different search.
          </div>
        ) : (
          <div
            className={styles.chipGrid}
            role="group"
            aria-label="Modality categories"
          >
            {state.visibleChips.map((label) => {
              const isSelected = state.selection.includes(label);
              const isPrimary = isSelected && label === state.firstSelected;
              const isDisabled = !isSelected && state.isAtLimit;

              return (
                <button
                  key={label}
                  type="button"
                  className={clsx(
                    styles.chipButton,
                    isSelected && styles.chipButtonSelected,
                    isPrimary && styles.chipButtonPrimary,
                  )}
                  aria-pressed={isSelected}
                  data-primary={isPrimary ? 'true' : undefined}
                  disabled={isDisabled}
                  onClick={() => vm.dispatch('ToggleChip', label)}
                >
                  {isPrimary ? `★ ${label}` : label}
                </button>
              );
            })}
          </div>
        )}

        {!state.hasSearchQuery && state.hasCollapsibleChips ? (
          <button
            type="button"
            className={styles.showToggle}
            onClick={vm.lazyDispatch('ToggleShowAll')}
            data-testid="modality-show-toggle"
          >
            {state.showAll
              ? 'Show fewer'
              : `Show all (${state.totalChipCount})`}
          </button>
        ) : null}

        <div className={styles.counterRow}>
          <Typography
            variant="body"
            size="small"
            color="N-80"
            className={styles.counterLabel}
          >
            {state.counterLabel}
          </Typography>
          <div className={styles.progressBar} aria-hidden>
            <div
              className={styles.progressBarFill}
              style={{ width: `${state.progressPercent}%` }}
            />
          </div>
        </div>

        <button
          type="button"
          className={styles.continueButton}
          disabled={!state.canContinue}
          onClick={() => onSubmit(state.selection)}
          data-testid="modality-continue"
        >
          Continue
        </button>
      </div>
    </div>
  );
};
